import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { siteConfig } from "../config/siteConfig";

const CART_STORAGE_KEY = `${siteConfig.storagePrefix}.cart`;
const WISHLIST_STORAGE_KEY = `${siteConfig.storagePrefix}.wishlist`;

export const CartContext = createContext();

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing. The in-memory state still works.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(CART_STORAGE_KEY).filter(
      (item) => Number.isInteger(item?.id) && Number(item?.quantity) > 0
    )
  );
  const [wishlist, setWishlist] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY)
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0)
  );

  useEffect(() => {
    persistArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(WISHLIST_STORAGE_KEY, wishlist);
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size = "", color = "") => {
    const id = Number(productId);
    const safeQuantity = Math.max(1, Math.floor(Number(quantity) || 1));
    if (!Number.isInteger(id) || id < 1) return;

    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) =>
          item.id === id && item.size === size && item.color === color
      );

      if (existingItem) {
        return prevItems.map((item) =>
          item.id === id && item.size === size && item.color === color
            ? { ...item, quantity: item.quantity + safeQuantity }
            : item
        );
      }

      return [...prevItems, { id, size, color, quantity: safeQuantity }];
    });
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    const id = Number(productId);
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) =>
          !(item.id === id && item.size === size && item.color === color)
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const id = Number(productId);
    const nextQuantity = Math.floor(Number(quantity) || 0);
    setCartItems((prevItems) => {
      if (nextQuantity <= 0) {
        return prevItems.filter(
          (item) =>
            !(item.id === id && item.size === size && item.color === color)
        );
      }
      return prevItems.map((item) =>
        item.id === id && item.size === size && item.color === color
          ? { ...item, quantity: nextQuantity }
          : item
      );
    });
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addToWishlist = useCallback((productId) => {
    const id = Number(productId);
    if (!Number.isInteger(id) || id < 1) return;
    setWishlist((items) => (items.includes(id) ? items : [...items, id]));
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = Number(productId);
    setWishlist((items) => items.filter((itemId) => itemId !== id));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = Number(productId);
    if (!Number.isInteger(id) || id < 1) return;
    setWishlist((items) =>
      items.includes(id)
        ? items.filter((itemId) => itemId !== id)
        : [...items, id]
    );
  }, []);

  const value = useMemo(
    () => ({
      cartItems,
      wishlist,
      cartQuantity: cartItems.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      ),
      wishlistCount: wishlist.length,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      clearCart,
      updateQuantity,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlist.includes(Number(productId)),
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
};
